import { useEffect, useRef, useState } from 'react';
import { Loader2, Mail, Plus, RotateCcw, Save, Store, Trash2, Upload, Link2, Crop } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import ImageAdjustDialog from './ImageAdjustDialog';

/**
 * Store Settings — a storefront's identity and the return policy it promises buyers.
 *
 * Both were only ever collected during the vendor application, so an owner who wanted to
 * change them afterwards had nowhere to go (owner, Jun 2026).
 */
const POLICY_DEFAULTS = {
  accepts_returns: false,
  window_days: 30,
  resolution: 'refund',
  return_shipping_paid_by: 'buyer',
  restocking_fee_percent: 0,
  non_returnable_note: '',
  excludes_custom: true,
  excludes_digital: true,
  process_steps: '',
  refund_timeframe: '',
  exceptions_note: '',
  returns_email: '',
};

const field = {
  width: '100%', padding: '9px 11px', borderRadius: 8, fontSize: 13,
  background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(255,255,255,0.12)',
  color: '#f5f0e8', fontFamily: "'Source Sans 3', sans-serif",
};
const label = { display: 'block', fontSize: 12, color: 'rgba(245,240,232,0.55)', marginBottom: 6 };
const hint = { fontSize: 11, color: 'rgba(245,240,232,0.4)', margin: '6px 0 0', lineHeight: 1.55 };
const card = {
  background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 14, padding: 18, marginBottom: 20,
};
const sectionHead = { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 };
const saveBtn = {
  display: 'inline-flex', alignItems: 'center', gap: 7, marginTop: 18,
  padding: '9px 16px', borderRadius: 8, border: 'none', background: '#1f7a5c',
  color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer',
};

const Toggle = ({ checked, onChange, children, testId }) => (
  <label style={{ display: 'flex', alignItems: 'flex-start', gap: 9, fontSize: 13, color: 'rgba(245,240,232,0.8)', cursor: 'pointer' }}>
    <input
      type="checkbox"
      checked={checked}
      onChange={e => onChange(e.target.checked)}
      data-testid={testId}
      style={{ marginTop: 2, width: 15, height: 15, accentColor: '#1f7a5c', cursor: 'pointer' }}
    />
    <span>{children}</span>
  </label>
);

const slugify = (name) => (name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

export default function StoreSettingsPanel({ vendorData, onUpdate }) {
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
  const vendorId = vendorData?.id;
  const logoInput = useRef(null);
  const bannerInput = useRef(null);

  const [identity, setIdentity] = useState({ store_name: '', description: '', low_stock_threshold: 3 });
  const [images, setImages] = useState({ avatar_url: null, banner_url: null });
  const [adjusting, setAdjusting] = useState(null);
  const [policy, setPolicy] = useState(POLICY_DEFAULTS);
  const [suggested, setSuggested] = useState(null);
  const [savingId, setSavingId] = useState(false);
  const [savingPolicy, setSavingPolicy] = useState(false);
  const [notify, setNotify] = useState([]);
  const [savingNotify, setSavingNotify] = useState(false);
  const [uploading, setUploading] = useState(null);

  useEffect(() => {
    if (!vendorData) return;
    setIdentity({
      store_name: vendorData.store_name || '',
      description: vendorData.description || '',
      low_stock_threshold: vendorData.low_stock_threshold ?? 3,
      free_shipping_threshold: vendorData.free_shipping_threshold ?? '',
    });
    setImages({
      avatar_url: vendorData.avatar_url || null,
      banner_url: vendorData.banner_url || null,
      avatar_original_url: vendorData.avatar_original_url || null,
      banner_original_url: vendorData.banner_original_url || null,
      avatar_crop: vendorData.avatar_crop || null,
      banner_crop: vendorData.banner_crop || null,
    });
    setPolicy({ ...POLICY_DEFAULTS, ...(vendorData.return_policy || {}) });
    setNotify(vendorData.notify_emails || []);
  }, [vendorData?.id, vendorData?.return_policy, vendorData?.store_name]);

  // New stores start from suggested wording rather than three empty boxes, so a policy is
  // never half-written when the first order lands.
  useEffect(() => {
    let live = true;
    axios.get(`${API}/marketplace/return-policy-defaults`)
      .then(({ data }) => {
        if (!live) return;
        setSuggested(data);
        setPolicy(p => ({
          ...p,
          process_steps: p.process_steps || data.process_steps,
          refund_timeframe: p.refund_timeframe || data.refund_timeframe,
          exceptions_note: p.exceptions_note || data.exceptions_note,
        }));
      })
      .catch(() => {});
    return () => { live = false; };
  }, [vendorData?.id]);

  const set = (key, value) => setPolicy(p => ({ ...p, [key]: value }));

  const saveIdentity = async () => {
    if (!identity.store_name.trim()) return toast.error('Your store needs a name');
    setSavingId(true);
    try {
      const { data } = await axios.patch(
        `${API}/vendor/me?vendor_id=${vendorId}`,
        { store_name: identity.store_name.trim(), description: identity.description,
          low_stock_threshold: Math.max(0, parseInt(identity.low_stock_threshold, 10) || 0),
          free_shipping_threshold: identity.free_shipping_threshold === ''
            ? null : Math.max(0, parseFloat(identity.free_shipping_threshold) || 0) },
        { headers },
      );
      const renamed = data?.store_slug && data.store_slug !== vendorData.store_slug;
      toast.success(renamed ? `Saved — your store is now at /store/${data.store_slug}` : 'Store details saved');
      onUpdate?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "We couldn't save your store details");
    } finally {
      setSavingId(false);
    }
  };

  const savePolicy = async () => {
    setSavingPolicy(true);
    try {
      await axios.patch(
        `${API}/vendor/me?vendor_id=${vendorId}`,
        {
          return_policy: {
            ...policy,
            non_returnable_note: policy.non_returnable_note || null,
            process_steps: policy.process_steps || null,
            refund_timeframe: policy.refund_timeframe || null,
            exceptions_note: policy.exceptions_note || null,
            returns_email: policy.returns_email || null,
          },
        },
        { headers },
      );
      toast.success('Return policy saved');
      onUpdate?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "We couldn't save your return policy");
    } finally {
      setSavingPolicy(false);
    }
  };

  const saveNotify = async () => {
    const rows = notify
      .map(r => ({ ...r, email: (r.email || '').trim().toLowerCase(), name: (r.name || '').trim() }))
      .filter(r => r.email);
    const bad = rows.find(r => !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(r.email));
    if (bad) return toast.error(`"${bad.email}" doesn't look like an email address`);
    const seen = new Set();
    const twice = rows.find(r => (seen.has(r.email) ? true : (seen.add(r.email), false)));
    if (twice) return toast.error(`${twice.email} is on the list twice`);
    setSavingNotify(true);
    try {
      await axios.patch(`${API}/vendor/me?vendor_id=${vendorId}`, { notify_emails: rows },
        { headers });
      setNotify(rows);
      toast.success(rows.length
        ? `Saved — ${rows.length} other ${rows.length === 1 ? 'person' : 'people'} will be told`
        : 'Saved — only you will be told about orders now');
      onUpdate?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "We couldn't save your notification list");
    } finally {
      setSavingNotify(false);
    }
  };

  // A picked file opens the adjuster first; the upload happens when they are happy with it.
  const pickFile = (kind, file) => {
    if (!file) return;
    setAdjusting({ kind, src: URL.createObjectURL(file), file, frame: null });
  };

  const closeAdjuster = () => {
    setAdjusting(a => {
      if (a?.file && a.src?.startsWith('blob:')) URL.revokeObjectURL(a.src);
      return null;
    });
  };

  // Reposition the picture already on the store, without asking for the file again. The stored
  // URL redirects to S3, which answers without CORS headers, so the canvas cannot read those
  // pixels — ?proxy=1 streams the same file through the backend instead.
  const readjust = (kind) => {
    const stored = kind === 'logo' ? images.avatar_original_url : images.banner_original_url;
    if (!stored) return;
    const src = stored.startsWith('blob:')
      ? stored
      : `${process.env.REACT_APP_BACKEND_URL}${stored}${stored.includes('?') ? '&' : '?'}proxy=1`;
    setAdjusting({ kind, src, file: null, frame: kind === 'logo' ? images.avatar_crop : images.banner_crop });
  };

  const upload = async (kind, blob, frame, original) => {
    setUploading(kind);
    const body = new FormData();
    body.append('file', new File([blob], `${kind}.png`, { type: 'image/png' }));
    if (original) body.append('original', original);
    if (frame) body.append('crop', JSON.stringify(frame));
    try {
      const { data } = await axios.post(`${API}/vendor/${kind}?vendor_id=${vendorId}`, body, { headers });
      setImages(i => ({
        ...i,
        [kind === 'logo' ? 'avatar_url' : 'banner_url']: data.file_url,
        [kind === 'logo' ? 'avatar_crop' : 'banner_crop']: frame || null,
      }));
      toast.success(kind === 'logo' ? 'Logo updated' : 'Banner updated');
      closeAdjuster();
      onUpdate?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || `We couldn't upload your ${kind}`);
    } finally {
      setUploading(null);
    }
  };

  if (!vendorData) {
    return (
      <p style={{ color: 'rgba(245,240,232,0.5)', fontSize: 13 }} data-testid="store-settings-no-store">
        Open a storefront first and its settings will appear here.
      </p>
    );
  }

  const nextSlug = slugify(identity.store_name);
  const renaming = nextSlug && nextSlug !== vendorData.store_slug;

  return (
    <div data-testid="store-settings-panel">
      <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 24, marginBottom: 8 }}>
        Store Settings
      </h2>
      <p style={{ color: 'rgba(245,240,232,0.45)', fontSize: 13, marginBottom: 24 }}>
        How {vendorData.store_name} looks to shoppers, and what you promise them about returns.
      </p>

      {/* ── Identity ─────────────────────────────────────────────── */}
      <div style={card}>
        <div style={sectionHead}>
          <Store size={16} style={{ color: '#6ee7c7' }} />
          <strong style={{ fontSize: 15, color: '#f5f0e8' }}>Store details</strong>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={label}>Store name</label>
          <input
            style={field}
            value={identity.store_name}
            onChange={e => setIdentity(i => ({ ...i, store_name: e.target.value }))}
            data-testid="store-name-input"
          />
          <p style={hint} data-testid="store-address-hint">
            <Link2 size={11} style={{ display: 'inline', marginRight: 4, verticalAlign: -1 }} />
            {renaming ? (
              <>Your address becomes <strong>/store/{nextSlug}</strong> when you save.
                The old one keeps working, so any link you&apos;ve already shared still finds you.</>
            ) : (
              <>Your address is <strong>/store/{vendorData.store_slug}</strong>. Rename the store and
                the address follows — old links keep working.</>
            )}
          </p>
        </div>

        <div>
          <label style={label}>Store description</label>
          <textarea
            rows={3}
            style={{ ...field, resize: 'vertical' }}
            placeholder="What you make, who it's for, and what makes it yours."
            value={identity.description}
            onChange={e => setIdentity(i => ({ ...i, description: e.target.value }))}
            data-testid="store-description-input"
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 14, marginTop: 18 }}>
          <div>
            <label style={label}>Logo</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              {images.avatar_url
                ? <img src={images.avatar_url} alt="Store logo" data-testid="store-logo-preview"
                    style={{ width: 54, height: 54, borderRadius: '50%', objectFit: 'cover' }} />
                : <div style={{ width: 54, height: 54, borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }} />}
              <input ref={logoInput} type="file" accept="image/*" hidden
                onChange={e => { pickFile('logo', e.target.files?.[0]); e.target.value = ''; }} data-testid="store-logo-file" />
              <button onClick={() => logoInput.current?.click()} disabled={uploading === 'logo'}
                data-testid="store-logo-upload-btn"
                style={{ ...saveBtn, marginTop: 0, background: 'rgba(255,255,255,0.1)' }}>
                {uploading === 'logo' ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
                Change
              </button>
              {images.avatar_original_url && (
                <button onClick={() => readjust('logo')} disabled={uploading === 'logo'}
                  data-testid="store-logo-adjust-btn"
                  style={{ ...saveBtn, marginTop: 0, background: 'rgba(255,255,255,0.1)' }}>
                  <Crop size={13} />
                  Reposition
                </button>
              )}
            </div>
            <p style={hint}>
              The round image on your storefront and beside your store name everywhere it appears.
              Square works best — roughly 400×400. You can drag and zoom it into place after
              choosing the file.
            </p>
          </div>
          <div>
            <label style={label}>Banner</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              {images.banner_url
                ? <img src={images.banner_url} alt="Store banner" data-testid="store-banner-preview"
                    style={{ width: 96, height: 44, borderRadius: 6, objectFit: 'cover' }} />
                : <div style={{ width: 96, height: 44, borderRadius: 6, background: 'rgba(255,255,255,0.08)' }} />}
              <input ref={bannerInput} type="file" accept="image/*" hidden
                onChange={e => { pickFile('banner', e.target.files?.[0]); e.target.value = ''; }} data-testid="store-banner-file" />
              <button onClick={() => bannerInput.current?.click()} disabled={uploading === 'banner'}
                data-testid="store-banner-upload-btn"
                style={{ ...saveBtn, marginTop: 0, background: 'rgba(255,255,255,0.1)' }}>
                {uploading === 'banner' ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
                Change
              </button>
              {images.banner_original_url && (
                <button onClick={() => readjust('banner')} disabled={uploading === 'banner'}
                  data-testid="store-banner-adjust-btn"
                  style={{ ...saveBtn, marginTop: 0, background: 'rgba(255,255,255,0.1)' }}>
                  <Crop size={13} />
                  Reposition
                </button>
              )}
            </div>
            <p style={hint}>
              The wide image across the top of your storefront, and behind your logo on your
              store card in the marketplace. Wide and simple works best — roughly 1600×400, and
              you can drag and zoom it into place after choosing the file.
            </p>
          </div>
        </div>

        <div style={{ marginTop: 18, maxWidth: 240 }}>
          <label style={label}>Warn me when stock reaches</label>
          <input
            type="number" min="0" max="9999"
            style={field}
            value={identity.low_stock_threshold}
            onChange={e => setIdentity(i => ({ ...i, low_stock_threshold: e.target.value }))}
            data-testid="low-stock-threshold-input"
          />
          <p style={hint}>
            Any product — or any single type of one — at or below this count is flagged on your
            Products page, so you restock before it sells out rather than after.
          </p>
        </div>

        <div style={{ marginTop: 18, maxWidth: 240 }}>
          <label style={label}>Free shipping over</label>
          <input
            type="number" min="0" step="1"
            placeholder="Leave blank for none"
            style={field}
            value={identity.free_shipping_threshold}
            onChange={e => setIdentity(i => ({ ...i, free_shipping_threshold: e.target.value }))}
            data-testid="free-shipping-threshold-input"
          />
          <p style={hint}>
            Baskets from your store at or above this figure post free. You still buy the label,
            so set it where the postage comes out of a bigger order. Blank switches it off.
          </p>
        </div>

        <button onClick={saveIdentity} disabled={savingId} data-testid="store-identity-save-btn" style={saveBtn}>
          {savingId ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
          {savingId ? 'Saving…' : 'Save store details'}
        </button>
      </div>

      {/* ── Who else hears about orders ──────────────────────────── */}
      <div style={card} data-testid="order-notify-card">
        <div style={sectionHead}>
          <Mail size={16} style={{ color: '#6ee7c7' }} />
          <strong style={{ fontSize: 15, color: '#f5f0e8' }}>Who else hears about orders</strong>
        </div>
        <p style={{ ...hint, margin: '0 0 14px' }}>
          Add a shipper, an assistant, anyone who needs to know. They get a packing copy —
          what was bought and where it goes, <strong>never your payout or earnings</strong>.
          You are always told as well. Up to five.
        </p>

        {notify.length === 0 && (
          <p style={{ ...hint, margin: '0 0 14px' }} data-testid="order-notify-empty">
            Nobody else yet — only you are told when a sale comes in.
          </p>
        )}

        {notify.map((row, i) => (
          <div key={i} style={{
            border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: 14,
            marginBottom: 12,
          }} data-testid={`order-notify-row-${i}`}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 12 }}>
              <div>
                <label style={label}>Email address</label>
                <input
                  type="email" style={field} value={row.email || ''}
                  placeholder="shipper@example.com"
                  onChange={e => setNotify(n => n.map((r, x) => x === i ? { ...r, email: e.target.value } : r))}
                  data-testid={`order-notify-email-${i}`}
                />
              </div>
              <div>
                <label style={label}>Name (optional)</label>
                <input
                  type="text" style={field} value={row.name || ''} placeholder="Who is this?"
                  onChange={e => setNotify(n => n.map((r, x) => x === i ? { ...r, name: e.target.value } : r))}
                  data-testid={`order-notify-name-${i}`}
                />
              </div>
            </div>
            <div style={{ marginTop: 12 }}>
              {[['new_order', 'A new order comes in'],
                ['unshipped_reminder', 'An order has been sitting unshipped'],
                ['return_request', 'A buyer asks to return something']].map(([key, text]) => (
                <Toggle key={key} checked={!!row[key]}
                  onChange={v => setNotify(n => n.map((r, x) => x === i ? { ...r, [key]: v } : r))}
                  testId={`order-notify-${key}-${i}`}>
                  {text}
                </Toggle>
              ))}
            </div>
            <button
              onClick={() => setNotify(n => n.filter((_, x) => x !== i))}
              data-testid={`order-notify-remove-${i}`}
              style={{
                ...saveBtn, marginTop: 12, background: 'rgba(255,255,255,0.08)',
                color: 'rgba(245,240,232,0.75)',
              }}>
              <Trash2 size={13} /> Remove
            </button>
          </div>
        ))}

        {notify.length < 5 && (
          <button
            onClick={() => setNotify(n => [...n, {
              email: '', name: '', new_order: true, unshipped_reminder: false,
              return_request: false,
            }])}
            data-testid="order-notify-add-btn"
            style={{ ...saveBtn, marginTop: 0, background: 'rgba(255,255,255,0.1)' }}>
            <Plus size={13} /> Add someone
          </button>
        )}

        <button onClick={saveNotify} disabled={savingNotify} data-testid="order-notify-save-btn"
          style={{ ...saveBtn, marginLeft: notify.length < 5 ? 10 : 0 }}>
          {savingNotify ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
          {savingNotify ? 'Saving…' : 'Save notification list'}
        </button>
      </div>

      {/* ── Return policy ────────────────────────────────────────── */}
      <div style={card}>
        <div style={sectionHead}>
          <RotateCcw size={16} style={{ color: '#6ee7c7' }} />
          <strong style={{ fontSize: 15, color: '#f5f0e8' }}>Return policy</strong>
        </div>

        <Toggle
          checked={policy.accepts_returns}
          onChange={v => set('accepts_returns', v)}
          testId="return-accepts-toggle"
        >
          I accept returns
          <span style={{ display: 'block', fontSize: 11, color: 'rgba(245,240,232,0.45)', marginTop: 2 }}>
            Leave this off for goods that genuinely can&apos;t come back — supplements, consumables,
            anything opened. Say why in the note below so buyers know before they order.
          </span>
        </Toggle>

        {policy.accepts_returns && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 14, marginTop: 18 }}>
            <div>
              <label style={label}>Days to ask, after delivery</label>
              <input
                type="number" min={1} max={365} style={field}
                value={policy.window_days}
                onChange={e => set('window_days', parseInt(e.target.value, 10) || 0)}
                data-testid="return-window-days"
              />
            </div>
            <div>
              <label style={label}>What the buyer gets</label>
              <select
                style={field} value={policy.resolution}
                onChange={e => set('resolution', e.target.value)}
                data-testid="return-resolution"
              >
                <option value="refund">Refund</option>
                <option value="replace">Replacement</option>
                <option value="either">Their choice of either</option>
              </select>
            </div>
            <div>
              <label style={label}>Return shipping paid by</label>
              <select
                style={field} value={policy.return_shipping_paid_by}
                onChange={e => set('return_shipping_paid_by', e.target.value)}
                data-testid="return-shipping-payer"
              >
                <option value="buyer">The buyer</option>
                <option value="vendor">Me</option>
              </select>
            </div>
            <div>
              <label style={label}>Restocking fee</label>
              <div style={{ position: 'relative' }}>
                <input
                  type="number" min={0} max={50} step={0.5} style={field}
                  value={policy.restocking_fee_percent}
                  onChange={e => set('restocking_fee_percent', parseFloat(e.target.value) || 0)}
                  data-testid="return-restocking-fee"
                />
                <span style={{ position: 'absolute', right: 11, top: 9, fontSize: 13, color: 'rgba(245,240,232,0.4)' }}>%</span>
              </div>
            </div>
          </div>
        )}

        <div style={{ display: 'grid', gap: 10, marginTop: 18 }}>
          <Toggle
            checked={policy.excludes_custom}
            onChange={v => set('excludes_custom', v)}
            testId="return-excludes-custom"
          >
            Made-to-order and personalised items are final sale
          </Toggle>
          <Toggle
            checked={policy.excludes_digital}
            onChange={v => set('excludes_digital', v)}
            testId="return-excludes-digital"
          >
            Digital downloads are final sale once delivered
          </Toggle>
          <p style={hint}>
            You can also mark a single item final sale on the product itself — every product is
            eligible for returns until you switch that off.
          </p>
        </div>

        <div style={{ marginTop: 18 }}>
          <label style={label}>What can&apos;t be returned, in your words (shown to buyers)</label>
          <textarea
            rows={2}
            style={{ ...field, resize: 'vertical' }}
            placeholder="e.g. Supplements and consumables cannot be returned once the seal is broken."
            value={policy.non_returnable_note || ''}
            onChange={e => set('non_returnable_note', e.target.value)}
            data-testid="return-note"
          />
        </div>

        <div style={{ marginTop: 18 }}>
          <label style={label}>How to start a return, step by step</label>
          <textarea
            rows={6}
            style={{ ...field, resize: 'vertical', lineHeight: 1.6 }}
            value={policy.process_steps || ''}
            onChange={e => set('process_steps', e.target.value)}
            data-testid="return-process-steps"
          />
          <p style={hint}>
            Suggested wording — make it yours, or clear it. Include anything they must send you
            (photos, order number, an authorization request) before shipping something back.
          </p>
        </div>

        <div style={{ marginTop: 18 }}>
          <label style={label}>How long refunds and exchanges take</label>
          <textarea
            rows={3}
            style={{ ...field, resize: 'vertical', lineHeight: 1.6 }}
            value={policy.refund_timeframe || ''}
            onChange={e => set('refund_timeframe', e.target.value)}
            data-testid="return-refund-timeframe"
          />
          <p style={hint}>Counted from the day the return reaches you, not the day they posted it.</p>
        </div>

        <div style={{ marginTop: 18 }}>
          <label style={label}>Damaged, defective or incorrect items</label>
          <textarea
            rows={4}
            style={{ ...field, resize: 'vertical', lineHeight: 1.6 }}
            value={policy.exceptions_note || ''}
            onChange={e => set('exceptions_note', e.target.value)}
            data-testid="return-exceptions-note"
          />
          <p style={hint}>
            Your words, your call. Tell buyers how you handle something that arrives broken or
            wrong — replace it, refund it, ask for photos first. The platform does not override
            what you write here.
          </p>
        </div>

        <div style={{ marginTop: 18, maxWidth: 340 }}>
          <label style={label}>Returns email</label>
          <input
            type="email"
            style={field}
            placeholder="returns@yourstore.com"
            value={policy.returns_email || ''}
            onChange={e => set('returns_email', e.target.value)}
            data-testid="return-email"
          />
          <p style={hint}>Where buyers write about a return. Shown on your storefront.</p>
        </div>

        <button onClick={savePolicy} disabled={savingPolicy} data-testid="store-settings-save-btn" style={saveBtn}>
          {savingPolicy ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
          {savingPolicy ? 'Saving…' : 'Save return policy'}
        </button>
      </div>

      {adjusting && (
        <ImageAdjustDialog
          open
          onOpenChange={(next) => { if (!next) closeAdjuster(); }}
          src={adjusting.src}
          kind={adjusting.kind}
          initialCrop={adjusting.frame}
          saving={uploading === adjusting.kind}
          onSave={(blob, frame) => upload(adjusting.kind, blob, frame, adjusting.file)}
        />
      )}
    </div>
  );
}
